import { useCallback, useEffect, useMemo, useState } from "react";

import { Heading } from "@magnetic/heading";
import { Toggle } from "@magnetic/toggle";

import { Modal } from "src/components";
import { Button } from "src/components/buttons";
import { ErrorNotification } from "src/components/errorNotification";
import { HFlex } from "src/components/hFlex";
import { LabelFieldContainer } from "src/components/labelFieldContainer";
import { Select } from "src/components/select";
import { VFlex } from "src/components/vFlex";
import { ApiError } from "src/core/apiError";
import { Bilge } from "src/gen/rest/bilge/BilgeService";
import {
  SetLinkedAccountRequest,
  SetLinkedAccountResponse
} from "src/gen/schema/bilge/api";
import { AdminState } from "src/gen/schema/models/global";
import { User as Bot } from "src/gen/schema/models/user";

import { usePasscodeValidation } from "./hooks/useBotPasscodeValidation";
import { BindingCodeDetails } from "./bindingCodeDetails";

import "./bindBotModal.scss";

interface BindBotModalProps {
  readonly show: boolean;
  readonly onClose: () => void;
  readonly onSuccess: (response: SetLinkedAccountResponse) => void;
  readonly bots: Bot[];
}

export const BindBotModal = ({
  show,
  onClose,
  onSuccess,
  bots
}: BindBotModalProps) => {
  const [error, setError] = useState<ApiError | undefined>();
  const [selectedBot, setSelectedBot] = useState<string>("");
  const [enabled, setEnabled] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);

  const {
    passcode,
    passcodeError,
    error: passcodeValidationError,
    validating: validatingPasscode,
    candidateInfo,
    handlePasscodeChange,
    resetPasscode
  } = usePasscodeValidation();

  const handleBind = useCallback(() => {
    setLoading(true);
    setError(undefined);

    const request = SetLinkedAccountRequest.create({
      passcode,
      userName: selectedBot,
      adminState: enabled ? AdminState.ENABLED : AdminState.DISABLED
    });

    Bilge.setLinkedAccount(request)
      .then((response) => {
        onSuccess(response);
        onClose();
      })
      .catch(setError)
      .finally(() => {
        setLoading(false);
      });
  }, [passcode, selectedBot, enabled, onClose, onSuccess]);

  const handleBotChange = useCallback(
    (value: string) => {
      setSelectedBot(value);
      if (error) {
        setError(undefined);
      }
    },
    [error]
  );

  const handleEnableChange = useCallback(() => {
    setEnabled((val) => !val);
  }, []);

  // Reset form when modal is closed
  useEffect(() => {
    if (!show) {
      setSelectedBot("");
      setEnabled(true);
      resetPasscode();
      setError(undefined);
      setLoading(false);
    }
  }, [show, resetPasscode]);

  // Prepare bot options for select dropdown (only enabled bots)
  const botOptions = useMemo(
    () =>
      bots
        .filter((bot) => bot.enabled)
        .map((bot) => ({
          label: bot.email,
          value: bot.email
        })),
    [bots]
  );

  // Determine if form is valid and ready to submit
  const canSubmit = useMemo(
    () =>
      Boolean(
        selectedBot &&
          candidateInfo &&
          !validatingPasscode &&
          !passcodeError &&
          !loading
      ),
    [selectedBot, candidateInfo, validatingPasscode, passcodeError, loading]
  );

  return (
    <Modal className="bind-bot-modal" isOpen={show} size="lg">
      <VFlex gap={24}>
        <Heading as="h2" size="primary">
          Bind bot user
        </Heading>

        <ErrorNotification error={error} />

        <VFlex gap="lg">
          <div className="bot-user-select">
            <LabelFieldContainer
              label="Select bot user"
              name="bot-user"
              required={true}
              size="md"
            >
              <Select
                name="bot-user"
                value={selectedBot}
                onChange={handleBotChange}
                options={botOptions}
                placeholder="Select a bot user"
                rootId="bot-user-select"
              />
            </LabelFieldContainer>
          </div>
          <BindingCodeDetails
            passcode={passcode}
            passcodeError={passcodeError}
            error={passcodeValidationError}
            validating={validatingPasscode}
            candidateInfo={candidateInfo}
            selectedBotName={selectedBot}
            onPasscodeChange={handlePasscodeChange}
          />

          <LabelFieldContainer
            label="Enable binding immediately"
            name="status"
            size="md"
          >
            <Toggle
              name="status"
              onClick={handleEnableChange}
              checked={enabled}
            >
              <span id="status-desc">{enabled ? "Enabled" : "Disabled"}</span>
            </Toggle>
          </LabelFieldContainer>
        </VFlex>

        <HFlex justify="flex-end" margin={{ top: 8 }}>
          <Button.Group>
            <Button
              kind="tertiary"
              name="modal-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              name="modal-save"
              onClick={handleBind}
              disabled={!canSubmit || loading}
            >
              Save
            </Button>
          </Button.Group>
        </HFlex>
      </VFlex>
    </Modal>
  );
};
